import axios from "axios";
import CryptoJS from "crypto-js";

const SECRET_KEY = "1234567890abcdef1234567890abcdef";

const TEAMS_API = process.env.REACT_APP_TEAMS_API;

/* ================= ENCRYPT ================= */
const encryptPayload = (data) => {
  return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
};

/* ================= DECRYPT ================= */
const decryptFunction = (ciphertext) => {
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  } catch (e) {
    console.error("Decryption failed:", e);
    return ciphertext;
  }
};

/* Note: unlike getRequest/postRequest, a 401 here must NOT force a global
   logout — a bad/missing function key on this endpoint would otherwise kick
   the user out of the whole app. We surface the error to the modal instead. */
const toFriendlyError = (error) => {
  if (error?.response) {
    const status = error.response.status;
    const message =
      status === 401
        ? "Not authorized to access the Teams API (check the function key)"
        : error.response.data?.message || `Server error (${status})`;
    const err = new Error(message);
    err.status = status;
    return err;
  }
  if (error?.request) {
    return new Error("No response from the Teams API");
  }
  return new Error(error?.message || "Something went wrong");
};

const authConfig = () => {
  const authToken = localStorage.getItem("authToken");
  return {
    headers: {
      Authorization: authToken ? `Bearer ${authToken}` : "",
    },
  };
};

/* ================= GET TEAMS =================
   Returns { teams: [{ teamName, director, agents }], teamNames: [], directors: [] } */
export const getTeams = async () => {
  if (!TEAMS_API) {
    throw new Error("REACT_APP_TEAMS_API is not configured");
  }

  try {
    const response = await axios.get(TEAMS_API, authConfig());

    if (!response || !response.data) {
      throw new Error("Empty response from server");
    }

    return decryptFunction(response.data);
  } catch (error) {
    throw toFriendlyError(error);
  }
};

/* ================= POST TEAMS =================
   Body is AES-encrypted. Response is plain JSON:
   { success, message, ... } — HTTP 400 on validation failure.

   addTeam:       { action: "addTeam", teamName, director, agents? }
   addMembers:    { action: "addMembers", teamName, agents }        -> { added, skipped }
   updateTeam:    { action: "updateTeam", teamName, newTeamName?, director? }
   renameMember:  { action: "renameMember", teamName, agent, newAgentName }
   removeMembers: { action: "removeMembers", teamName, agents }     -> { removed, notFound } */
export const postTeams = async (payload) => {
  if (!TEAMS_API) {
    throw new Error("REACT_APP_TEAMS_API is not configured");
  }

  const config = authConfig();
  config.headers["Content-Type"] = "text/plain";

  try {
    const response = await axios.post(
      TEAMS_API,
      encryptPayload(payload),
      config,
    );
    return response.data;
  } catch (error) {
    // 400 validation failures carry { success: false, message } — let the
    // modal show the server's message instead of treating it as a crash.
    if (error?.response?.status === 400 && error.response.data) {
      return error.response.data;
    }
    throw toFriendlyError(error);
  }
};
